import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { IoShareSocialOutline } from "react-icons/io5";
import { createRoot } from 'react-dom/client';
import { fetchAbstractByPMID } from '../utils/fetchRecommendedpapers';
import { translateWithBaidu } from '../utils/translation';
import { useTranslation } from 'react-i18next';

const copyImageToClipboard = async (imageUrl) => {
  try {
    console.log('Starting to copy image to clipboard');
    
    if (navigator.clipboard && navigator.clipboard.write) {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      console.log('Successfully copied using Clipboard API');
      return true;
    } 
    else {
      console.log('Clipboard API not available, falling back to download');
      const link = document.createElement('a');
      link.download = 'paper-share.png';
      link.href = imageUrl;
      link.click();
      return true;
    }
  } catch (err) {
    console.error('Failed to copy/download image:', err);
    try {
      const link = document.createElement('a');
      link.download = 'paper-share.png';
      link.href = imageUrl;
      link.click();
      return true;
    } catch (downloadErr) {
      console.error('Both clipboard and download failed:', downloadErr);
      return false;
    }
  }
};

const Notification = ({ message }) => (
  <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-black/75 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in-up">
    <p className="text-sm">{message}</p>
  </div>
);

const SharePoster = ({ title, authors, institution, metrics, PMID, referralCode, isDarkMode, hidden = false }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`fixed ${hidden ? 'opacity-0' : ''} left-[-9999px]`}
      style={{ width: '800px', height: '1100px' }}
    >
      <div className="w-full h-full relative overflow-hidden flex flex-col" style={{
        background: 'linear-gradient(145deg, #0c0e1a 0%, #111827 40%, #0f172a 100%)'
      }}>
        {/* Subtle gradient orbs */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-20" style={{
          background: 'radial-gradient(circle, rgba(99,102,241,0.4) 0%, transparent 70%)'
        }} />
        <div className="absolute bottom-20 left-0 w-64 h-64 rounded-full opacity-15" style={{
          background: 'radial-gradient(circle, rgba(6,182,212,0.4) 0%, transparent 70%)'
        }} />

        <div className="relative z-10 flex flex-col h-full p-10">
          {/* Compact header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-3xl font-bold text-white tracking-tight">{t('sharePoster.interestingArticle')}</div>
              <div className="text-sm text-slate-400 mt-1">{t('sharePoster.aiDriven')}</div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-indigo-400 tracking-wide">labcat.ai</div>
            </div>
          </div>

          {/* Paper card */}
          <div className="rounded-2xl p-7 mb-5 flex-shrink-0" style={{
            background: 'linear-gradient(135deg, rgba(30,41,59,0.8) 0%, rgba(15,23,42,0.9) 100%)',
            border: '1px solid rgba(99,102,241,0.2)'
          }}>
            <h2 className="text-[26px] text-white font-bold leading-snug mb-5">
              {title}
            </h2>

            <div className="space-y-2 mb-5">
              <p className="text-lg text-slate-300">
                {typeof authors === 'string' ? authors : authors.join(', ')}
              </p>
              {institution && (
                <p className="text-base text-slate-400">
                  {institution}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              {metrics.if && (
                <span className="px-3 py-1.5 rounded-full text-base font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/20">
                  IF: {metrics.if}
                </span>
              )}
              {metrics.jcrQ && (
                <span className="px-3 py-1.5 rounded-full text-base font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
                  Q: {metrics.jcrQ}
                </span>
              )}
              {metrics.casBlock && (
                <span className="px-3 py-1.5 rounded-full text-base font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/20">
                  {t('recommendedPDF.casLabel', 'CAS')}: {metrics.casBlock}{t('recommendedPDF.casZone', '')}
                </span>
              )}
            </div>
          </div>

          {/* QR + website row */}
          <div className="flex gap-5 mb-5">
            <div className="rounded-2xl p-5 flex items-center gap-5 flex-1" style={{
              background: 'linear-gradient(135deg, rgba(30,41,59,0.7) 0%, rgba(15,23,42,0.8) 100%)',
              border: '1px solid rgba(99,102,241,0.15)'
            }}>
              <div className="bg-white p-2 rounded-xl shrink-0">
                <QRCodeSVG
                  value={`https://pubmed.ncbi.nlm.nih.gov/${PMID}/`}
                  size={100}
                  level="L"
                  includeMargin={true}
                  fgColor="#1e293b"
                  bgColor="#FFFFFF"
                />
              </div>
              <div>
                <p className="text-base font-semibold text-slate-200">{t('sharePoster.scanQRCode')}</p>
                <p className="text-sm text-slate-400 mt-1 font-mono">
                  pubmed.ncbi.nlm.nih.gov/{PMID}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom section: website + referral side by side */}
          <div className="mt-auto flex gap-5">
            <div className="flex-1 rounded-2xl p-5" style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(30,41,59,0.6) 100%)',
              border: '1px solid rgba(99,102,241,0.2)'
            }}>
              <p className="text-lg font-bold text-indigo-300 mb-1">{t('sharePoster.visitWebsite')}</p>
              <p className="text-sm text-slate-400">{t('sharePoster.experienceMore')}</p>
              <p className="text-sm text-slate-400">{t('sharePoster.increaseEfficiency')}</p>
            </div>

            {referralCode && (
              <div className="flex-1 rounded-2xl p-5" style={{
                background: 'linear-gradient(135deg, rgba(236,72,153,0.1) 0%, rgba(30,41,59,0.6) 100%)',
                border: '1px solid rgba(236,72,153,0.2)'
              }}>
                <p className="text-lg font-bold text-pink-300 mb-1">{t('sharePoster.exclusiveReward')}</p>
                <p className="text-sm text-slate-300">
                  {t('sharePoster.useMyCode')} <span className="text-pink-400 font-bold font-mono text-base">{referralCode}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">{t('sharePoster.newUserBonus')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RecommendedPDF = ({
  PMID,
  title,
  authors,
  journalName,
  institution,
  metrics,
  keyword,
  isDarkMode,
  onClick,
  pubDate,
  matchScore = null,
  reasons = [],
  featured = false,
  compact = false,
}) => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [abstract, setAbstract] = useState('');
  const [translatedAbstract, setTranslatedAbstract] = useState('');
  const [showAbstract, setShowAbstract] = useState(false);
  const [isLoadingAbstract, setIsLoadingAbstract] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('selectedLanguage') || 'zh');

  const { t, i18n } = useTranslation();

  // Initialize currentLanguage from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'zh';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleAbstractToggle = async (e) => {
    e.stopPropagation();
    setShowAbstract(!showAbstract);
    
    // Only fetch if we're showing and haven't loaded yet
    if (!showAbstract && !abstract && !isLoadingAbstract) {
      setIsLoadingAbstract(true);
      try {
        console.log('Fetching abstract for PMID:', PMID);
        const fetchedAbstract = await fetchAbstractByPMID(PMID);
        console.log('Received abstract:', fetchedAbstract);
        setAbstract(fetchedAbstract);
        
        // Automatically translate the abstract when it's fetched
        if (fetchedAbstract) {
          await translateAbstract(fetchedAbstract, currentLanguage);
        }
      } catch (error) {
        console.error('Error fetching abstract:', error);
      } finally {
        setIsLoadingAbstract(false);
      }
    }
  };

  // Listen for language changes in localStorage
  useEffect(() => {
    const checkLanguage = () => {
      const savedLanguage = localStorage.getItem('selectedLanguage') || 'zh';
      if (savedLanguage !== currentLanguage) {
        setCurrentLanguage(savedLanguage);
        
        // Retranslate the abstract if we have one
        if (abstract && abstract.trim() !== '') {
          translateAbstract(abstract, savedLanguage);
        }
      }
    };
    
    // Check initially
    checkLanguage();
    
    // Set up an interval to check periodically (every 1 second)
    const intervalId = setInterval(checkLanguage, 1000);
    
    return () => clearInterval(intervalId);
  }, [currentLanguage, abstract]);

  // Helper function to translate abstract
  const translateAbstract = async (abstractText, language) => {
    if (!abstractText || abstractText.trim() === '') return;
    
    setIsTranslating(true);
    try {
      const translation = await translateWithBaidu(abstractText, null, language);
      if (translation) {
        setTranslatedAbstract(translation);
      }
    } catch (error) {
      console.error('Error translating abstract:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const ensureReferralCode = async () => {
    if (referralCode) {
      return referralCode;
    }

    const token =
      localStorage.getItem('wechat_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('email_token');

    if (!token) {
      return '';
    }

    try {
      const response = await fetch('https://api.labcat.com.cn/redemption/referral-code', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return '';
      }

      const data = await response.json();
      const nextCode = data.referral_code || '';
      setReferralCode(nextCode);
      return nextCode;
    } catch (error) {
      console.error('Error getting referral code:', error);
      return '';
    }
  };

  // Function to safely handle authors
  const formatAuthors = (authors) => {
    if (Array.isArray(authors)) {
      return authors.join(', ');
    } else if (typeof authors === 'string') {
      return authors;
    }
    return 'Unknown';
  };

  // Function to format CAS block based on language
  const formatCasBlock = (block) => {
    if (!block) return null;
    
    // In English, show as "CAS: B1", in Chinese show as "中科院: 1区"
    const isEnglish = i18n.language === 'en';
    const cleanBlock = block.replace('B', '');
    
    return isEnglish ? `CAS: B${cleanBlock}` : `${cleanBlock}区`;
  };

  const generateShareImage = async (activeReferralCode = referralCode) => {
    // Create a container for our poster
    const container = document.createElement('div');
    document.body.appendChild(container);
    
    // Render the poster component
    const root = createRoot(container);
    root.render(
      <SharePoster 
        title={title}
        authors={authors}
        institution={institution}
        metrics={metrics}
        PMID={PMID}
        referralCode={activeReferralCode || 'LABCAT'}
        isDarkMode={isDarkMode}
      />
    );

    // Wait for fonts to load
    await document.fonts.ready;
    
    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const canvas = await html2canvas(container.firstChild, {
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
        backgroundColor: null
      });

      const imageUrl = canvas.toDataURL('image/png', 0.8);

      // Cleanup
      root.unmount();
      document.body.removeChild(container);

      return imageUrl;
    } catch (error) {
      console.error('Failed to generate image:', error);
      root.unmount();
      document.body.removeChild(container);
      throw error;
    }
  };

  const handleImageShare = async (e) => {
    e?.stopPropagation();
    setIsGeneratingImage(true);

    try {
      const activeReferralCode = await ensureReferralCode();
      const imageUrl = await generateShareImage(activeReferralCode);

      // Convert data URL to proper blob for sharing
      const byteString = atob(imageUrl.split(',')[1]);
      const mimeType = 'image/png';
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeType });
      const file = new File([blob], `${(title || 'paper').replace(/[^a-zA-Z0-9\u4e00-\u9fff]/g, '_').slice(0, 50)}.png`, { type: mimeType, lastModified: Date.now() });

      // Try native OS share sheet first (macOS/Windows/mobile)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          return; // User handled it via share sheet
        } catch (shareErr) {
          // User cancelled or share failed — fall through to clipboard
          if (shareErr.name === 'AbortError') return;
        }
      }

      // Fallback: copy to clipboard
      const success = await copyImageToClipboard(imageUrl);

      if (success) {
        setNotificationMessage(
          i18n.language === 'en'
            ? 'Poster copied to clipboard.'
            : '海报已复制到剪贴板。'
        );
      } else {
        setNotificationMessage(
          i18n.language === 'en'
            ? 'Could not copy automatically. A download was started instead.'
            : '自动复制失败，已改为下载。'
        );
      }
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } catch (error) {
      console.error('Image share failed:', error);
      setNotificationMessage(
        i18n.language === 'en'
          ? 'Poster generation failed. Please try again.'
          : '海报生成失败，请稍后再试。'
      );
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const matchPercentage = typeof matchScore === 'number' ? Math.round(matchScore * 100) : null;
  const visibleReasons = reasons.filter(Boolean).slice(0, compact ? 1 : (featured ? 3 : 2));
  const cardSizeClass = compact
    ? 'h-[260px] w-[272px] shrink-0 p-4'
    : featured
    ? 'min-h-[340px] p-5 md:p-6'
    : 'min-h-[340px] p-5';

  const matchTone = matchPercentage !== null && matchPercentage >= 75
    ? (isDarkMode ? 'from-emerald-500/20 to-cyan-500/10 text-emerald-200 border-emerald-400/30' : 'from-emerald-100 to-cyan-50 text-emerald-700 border-emerald-200')
    : (isDarkMode ? 'from-blue-500/20 to-fuchsia-500/10 text-blue-100 border-blue-400/30' : 'from-blue-100 to-indigo-50 text-blue-700 border-blue-200');

  if (compact) {
    return (
      <>
        <div
          className={`group relative isolate overflow-hidden rounded-[24px] border backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
            isDarkMode
              ? 'border-white/10 bg-[#0f1729]/80 shadow-[0_22px_70px_rgba(3,7,18,0.45)]'
              : 'border-slate-200/80 bg-white/85 shadow-[0_22px_70px_rgba(148,163,184,0.22)]'
          } ${cardSizeClass}`}
          onClick={onClick}
        >
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.18),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(244,63,94,0.12),transparent_28%)]'
              : 'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(244,114,182,0.10),transparent_28%)]'
          }`} />

          <div className="relative flex h-full flex-col">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={`line-clamp-1 text-[10px] font-semibold uppercase tracking-[0.24em] ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  {keyword || t('homeTab.recommendationEngine', 'PubMed')}
                </p>
              </div>
              {pubDate && (
                <span className={`shrink-0 text-[11px] font-medium ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-500'
                }`}>
                  {pubDate}
                </span>
              )}
            </div>

            <h3 className={`mt-3 line-clamp-4 text-[1rem] font-semibold leading-[1.25rem] ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
              {title}
            </h3>

            <p className={`mt-3 line-clamp-1 text-[13px] ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              {formatAuthors(authors)}
            </p>

            <div className="mt-2 flex items-center justify-between gap-3">
              <p className={`line-clamp-1 text-[13px] font-medium ${
                isDarkMode ? 'text-slate-200' : 'text-slate-700'
              }`}>
                {journalName || t('homeTab.recommendationEngine', 'PubMed')}
              </p>
              {matchPercentage !== null && (
                <span className={`shrink-0 text-[11px] font-semibold ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  {matchPercentage}% match
                </span>
              )}
            </div>

            {matchPercentage !== null ? (
              <div className="mt-4">
                <div className={`mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span>{t('homeTab.similarity', 'Similarity')}</span>
                  <span>{matchPercentage}%</span>
                </div>
                <div className={`h-1.5 overflow-hidden rounded-full ${isDarkMode ? 'bg-white/8' : 'bg-slate-200'}`}>
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${
                      matchPercentage >= 75 ? 'from-emerald-400 via-cyan-400 to-blue-400' : 'from-blue-400 via-indigo-400 to-fuchsia-400'
                    }`}
                    style={{ width: `${Math.max(matchPercentage, 8)}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-4" />
            )}

            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
              <div className="flex flex-wrap gap-2">
                {metrics.if && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-800'
                  }`}>
                    IF: {metrics.if}
                  </span>
                )}
                {metrics.jcrQ && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-emerald-500/15 text-emerald-100' : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    Q: {metrics.jcrQ}
                  </span>
                )}
                {metrics.casBlock && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-amber-500/15 text-amber-100' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {formatCasBlock(metrics.casBlock)}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleImageShare(); }}
                disabled={isGeneratingImage}
                className={`shrink-0 inline-flex items-center justify-center rounded-full p-1.5
                  transition-all duration-200
                  ${isDarkMode
                    ? 'text-slate-400 hover:text-slate-200 hover:bg-white/10'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                  }
                  ${isGeneratingImage ? 'opacity-50 cursor-not-allowed animate-pulse' : ''}`}
                aria-label="Share as Image"
              >
                <IoShareSocialOutline className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {showNotification && (
          <Notification
            message={notificationMessage}
            onClose={() => setShowNotification(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div 
        className={`group relative isolate h-full overflow-hidden rounded-[24px] border backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
          isDarkMode
            ? 'border-white/10 bg-[#0f1729]/80 shadow-[0_22px_70px_rgba(3,7,18,0.45)]'
            : 'border-slate-200/80 bg-white/85 shadow-[0_22px_70px_rgba(148,163,184,0.22)]'
        } ${cardSizeClass}`}
        onClick={onClick}
      >
        <div className={`absolute inset-0 ${
          isDarkMode
            ? 'bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.18),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(244,63,94,0.12),transparent_28%)]'
            : 'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(244,114,182,0.10),transparent_28%)]'
        }`} />
        <div className={`absolute -right-10 top-4 h-28 w-28 rounded-full blur-3xl ${
          isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/70'
        }`} />

        <div className="relative flex h-full flex-col">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {keyword && (
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode
                    ? 'border-cyan-400/20 bg-cyan-400/10 text-cyan-200'
                    : 'border-cyan-200 bg-cyan-50 text-cyan-700'
                }`}>
                  {keyword}
                </span>
              )}
              {matchPercentage !== null && (
                <span className={`rounded-full border bg-gradient-to-r px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${matchTone}`}>
                  {matchPercentage}% match
                </span>
              )}
            </div>
            {pubDate && (
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                isDarkMode ? 'bg-white/6 text-slate-300' : 'bg-slate-100 text-slate-600'
              }`}>
                {pubDate}
              </span>
            )}
          </div>

          <h3 className={`mt-4 font-semibold leading-tight ${
            compact
              ? 'line-clamp-3 text-base'
              : featured
              ? 'line-clamp-3 text-[1.15rem] md:text-[1.3rem]'
              : 'line-clamp-3 text-[1.05rem]'
          } ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h3>

          <div className={`mt-4 space-y-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            <p className={compact ? 'line-clamp-2 text-[13px]' : 'line-clamp-2'}>
              <span className={`mr-2 inline-block text-[11px] font-semibold uppercase tracking-[0.18em] ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}>
                Authors:
              </span>
              {formatAuthors(authors)}
            </p>
            {!compact && institution && (
              <p className="line-clamp-2">
                <span className={`mr-2 inline-block text-[11px] font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  Institution:
                </span>
                {institution}
              </p>
            )}
          </div>

          {visibleReasons.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {visibleReasons.map((reason) => (
                <span
                  key={reason}
                  className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-white/6 text-slate-200' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {reason}
                </span>
              ))}
            </div>
          )}

          {matchPercentage !== null && (
            <div className="mt-5">
              <div className={`mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>
                <span>{t('homeTab.similarity', 'Similarity')}</span>
                <span>{matchPercentage}%</span>
              </div>
              <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? 'bg-white/8' : 'bg-slate-200'}`}>
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${
                    matchPercentage >= 75 ? 'from-emerald-400 via-cyan-400 to-blue-400' : 'from-blue-400 via-indigo-400 to-fuchsia-400'
                  }`}
                  style={{ width: `${Math.max(matchPercentage, 8)}%` }}
                />
              </div>
            </div>
          )}

          {!compact && (
            <button
              onClick={handleAbstractToggle}
              className={`mt-5 inline-flex self-start rounded-full border px-3 py-1.5 text-xs font-semibold
                transition-all duration-300 ease-in-out
                items-center gap-1.5
                ${isDarkMode 
                  ? 'border-cyan-700/30 bg-cyan-800/40 text-cyan-100 hover:bg-cyan-700/50' 
                  : 'border-cyan-200 bg-cyan-100/90 text-cyan-800 hover:bg-cyan-200/90'
                }
                backdrop-blur-sm shadow-sm
              `}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${showAbstract 
                ? 'bg-current animate-pulse' 
                : 'bg-current'}`}
              />
              {showAbstract ? t('recommendedPDF.hideAbstract') : t('recommendedPDF.showAbstract')}
            </button>
          )}

          {!compact && showAbstract && (
            <div className={`mt-4 rounded-[20px] border p-4 text-sm ${
              isDarkMode ? 'border-white/10 bg-black/20' : 'border-slate-200 bg-white/80'
            }`}>
              {isLoadingAbstract ? (
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('recommendedPDF.loading')}
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Translated Abstract */}
                  {abstract && (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {t('recommendedPDF.abstract')}
                        </h3>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                        }`}>
                          {t('recommendedPDF.machineTranslation')}
                        </span>
                      </div>
                      <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {isTranslating ? t('recommendedPDF.translating') : (translatedAbstract || t('recommendedPDF.translationFailed'))}
                      </p>
                    </div>
                  )}
                  
                  {/* Original Abstract */}
                  <div>
                    <h3 className={`font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {t('recommendedPDF.originalAbstract')}
                    </h3>
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {abstract || t('recommendedPDF.noAbstract')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-auto pt-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className={`line-clamp-1 ${compact ? 'text-[13px]' : 'text-sm'} font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                {journalName}
              </p>
              {PMID && (
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  isDarkMode ? 'bg-white/6 text-slate-300' : 'bg-slate-100 text-slate-600'
                }`}>
                  PMID {PMID}
                </span>
              )}
            </div>

            <div className="flex items-end justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {metrics.if && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-800'
                  }`}>
                    IF: {metrics.if}
                  </span>
                )}
                {metrics.jcrQ && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-emerald-500/15 text-emerald-100' : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    Q: {metrics.jcrQ}
                  </span>
                )}
                {metrics.casBlock && (
                  <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs leading-none ${
                    isDarkMode ? 'bg-amber-500/15 text-amber-100' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {formatCasBlock(metrics.casBlock)}
                  </span>
                )}
              </div>

              {!compact && (
                <button
                  onClick={handleImageShare}
                  disabled={isGeneratingImage}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold
                    transition-all duration-300 transform
                    ${isDarkMode 
                      ? 'border-blue-700/30 bg-blue-800/40 text-blue-100 hover:bg-blue-700/50' 
                      : 'border-blue-200 bg-blue-100/90 text-blue-800 hover:bg-blue-200/90'
                    }
                    ${isGeneratingImage ? 'opacity-50 cursor-not-allowed' : ''}
                    group relative overflow-hidden backdrop-blur-sm`}
                  aria-label="Share as Image"
                  title="Share as Image"
                >
                  {isGeneratingImage ? (
                    <span className="animate-pulse text-xs">{t('recommendedPDF.generating')}</span>
                  ) : (
                    <>
                      <IoShareSocialOutline 
                        className="h-3.5 w-3.5 transition-transform group-hover:rotate-6" 
                      />
                      <span>{t('recommendedPDF.shareImage')}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add notification */}
      {showNotification && (
        <Notification 
          message={notificationMessage} 
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};

export default RecommendedPDF;
